import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Global AI Tools Store — persists inputs, outputs, and loading states
 * across tab switches and navigation. Each tool gets its own slice.
 */
export const useAIToolsStore = create(
  persist(
    (set, get) => ({
      // Per-tool state: { [toolId]: { inputs: {}, output: null, loading: false, error: null, lastUpdated: null } }
      tools: {},

      setToolInput: (toolId, key, value) =>
        set(state => ({
          tools: {
            ...state.tools,
            [toolId]: {
              ...state.tools[toolId],
              inputs: { ...(state.tools[toolId]?.inputs || {}), [key]: value },
            },
          },
        })),

      setToolInputs: (toolId, inputs) =>
        set(state => ({
          tools: {
            ...state.tools,
            [toolId]: {
              ...state.tools[toolId],
              inputs: { ...(state.tools[toolId]?.inputs || {}), ...inputs },
            },
          },
        })),

      setToolOutput: (toolId, output) =>
        set(state => ({
          tools: {
            ...state.tools,
            [toolId]: {
              ...state.tools[toolId],
              output,
              loading: false,
              error: null,
              lastUpdated: new Date().toISOString(),
            },
          },
        })),

      setToolLoading: (toolId, loading) =>
        set(state => ({
          tools: {
            ...state.tools,
            [toolId]: { ...state.tools[toolId], loading },
          },
        })),

      setToolError: (toolId, error) =>
        set(state => ({
          tools: {
            ...state.tools,
            [toolId]: { ...state.tools[toolId], error, loading: false },
          },
        })),

      clearTool: (toolId) =>
        set(state => {
          const tools = { ...state.tools };
          delete tools[toolId];
          return { tools };
        }),

      getToolState: (toolId) => get().tools[toolId] || { inputs: {}, output: null, loading: false, error: null },
    }),
    {
      name: 'scalitos-ai-tools',
      partialize: (state) => ({
        tools: Object.fromEntries(
          Object.entries(state.tools).map(([k, v]) => [k, { inputs: v.inputs, output: v.output, lastUpdated: v.lastUpdated }])
        ),
      }),
    }
  )
);

/**
 * Chat History Store — persistent chat sessions per tool
 */
export const useChatStore = create(
  persist(
    (set, get) => ({
      // { [toolId]: { sessions: [{ id, title, messages: [{role, content, ts}], createdAt }], activeSessionId } }
      chats: {},

      getChat: (toolId) => get().chats[toolId] || { sessions: [], activeSessionId: null },

      createSession: (toolId, title) => {
        const id = `${toolId}-${Date.now()}`;
        set(state => {
          const chat = state.chats[toolId] || { sessions: [], activeSessionId: null };
          return {
            chats: {
              ...state.chats,
              [toolId]: {
                sessions: [{ id, title: title || 'New Chat', messages: [], createdAt: new Date().toISOString() }, ...chat.sessions],
                activeSessionId: id,
              },
            },
          };
        });
        return id;
      },

      setActiveSession: (toolId, sessionId) =>
        set(state => ({
          chats: {
            ...state.chats,
            [toolId]: { ...state.chats[toolId], activeSessionId: sessionId },
          },
        })),

      addMessage: (toolId, sessionId, message) =>
        set(state => {
          const chat = state.chats[toolId];
          if (!chat) return state;
          return {
            chats: {
              ...state.chats,
              [toolId]: {
                ...chat,
                sessions: chat.sessions.map(s =>
                  s.id === sessionId
                    ? { ...s, messages: [...s.messages, { ...message, ts: new Date().toISOString() }] }
                    : s
                ),
              },
            },
          };
        }),

      deleteSession: (toolId, sessionId) =>
        set(state => {
          const chat = state.chats[toolId];
          if (!chat) return state;
          const sessions = chat.sessions.filter(s => s.id !== sessionId);
          return {
            chats: {
              ...state.chats,
              [toolId]: {
                sessions,
                activeSessionId: chat.activeSessionId === sessionId ? sessions[0]?.id || null : chat.activeSessionId,
              },
            },
          };
        }),
    }),
    { name: 'scalitos-chat-history' }
  )
);

/**
 * Brand Memory Store — persistent brand context used across all tools
 */
export const useBrandMemoryStore = create(
  persist(
    (set, get) => ({
      brandName: '',
      niche: '',
      audience: '',
      tone: '',
      style: '',
      keywords: [],
      pastContentThemes: [],
      competitorNotes: '',

      setBrandField: (key, value) => set({ [key]: value }),
      setBrandContext: (ctx) => set(ctx),
      getBrandContext: () => {
        const s = get();
        return {
          brandName: s.brandName, niche: s.niche, audience: s.audience,
          tone: s.tone, style: s.style, keywords: s.keywords,
        };
      },
    }),
    { name: 'scalitos-brand-memory' }
  )
);
